import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { orderService } from '../api/supabase';

const Checkout = () => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    phone: ''
  });

  const { cartItems, getCartTotal, clearCart } = useCart();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const cartTotal = getCartTotal();
  const tax = cartTotal * 0.08;
  const shipping = cartTotal >= 50 ? 0 : 5.99;
  const total = cartTotal + tax + shipping;

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'CNY',
    }).format(price);
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    if (step === 1) {
      setStep(2);
      return;
    }

    setLoading(true);
    try {
      const shippingAddress = {
        first_name: formData.firstName,
        last_name: formData.lastName,
        address: formData.address,
        city: formData.city,
        state: formData.state,
        zip_code: formData.zipCode,
        country: 'USA',
        phone: formData.phone
      };

      await orderService.create({ shippingAddress });
      await clearCart();
      navigate('/orders');
    } catch (error) {
      console.error('Failed to create order:', error);
      alert('创建订单失败，请重试。');
    } finally {
      setLoading(false);
    }
  };

  if (cartItems.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">您的购物车是空的</h2>
          <button onClick={() => navigate('/products')} className="btn-primary">
            浏览商品
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">结账</h1>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Checkout Form */}
          <div className="lg:col-span-2">
            <form onSubmit={handleSubmit} className="space-y-6">
              {step === 1 && (
                <div className="bg-white rounded-lg shadow-soft p-6">
                  <h2 className="text-xl font-semibold text-gray-900 mb-4">收货信息</h2>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <input
                      type="text"
                      name="firstName"
                      placeholder="名 *"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      required
                      className="input-field"
                    />
                    <input
                      type="text"
                      name="lastName"
                      placeholder="姓 *"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      required
                      className="input-field"
                    />
                    <input
                      type="email"
                      name="email"
                      placeholder="邮箱 *"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="input-field"
                    />
                    <input
                      type="tel"
                      name="phone"
                      placeholder="电话 *"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                      className="input-field"
                    />
                    <input
                      type="text"
                      name="address"
                      placeholder="地址 *"
                      value={formData.address}
                      onChange={handleInputChange}
                      required
                      className="input-field md:col-span-2"
                    />
                    <input
                      type="text"
                      name="city"
                      placeholder="城市 *"
                      value={formData.city}
                      onChange={handleInputChange}
                      required
                      className="input-field"
                    />
                    <input
                      type="text"
                      name="state"
                      placeholder="州 *"
                      value={formData.state}
                      onChange={handleInputChange}
                      required
                      className="input-field"
                    />
                    <input
                      type="text"
                      name="zipCode"
                      placeholder="邮政编码 *"
                      value={formData.zipCode}
                      onChange={handleInputChange}
                      required
                      className="input-field"
                    />
                  </div>
                </div>
              )}

              {step === 2 && (
                <div className="bg-white rounded-lg shadow-soft p-6">
                  <h2 className="text-xl font-semibold text-gray-900 mb-4">支付信息</h2>
                  <p className="text-gray-600 mb-4">这是演示结账，不进行真实支付。</p>

                  <div className="space-y-4">
                    <input
                      type="text"
                      placeholder="卡号"
                      className="input-field"
                      disabled
                    />
                    <div className="grid grid-cols-3 gap-4">
                      <input
                        type="text"
                        placeholder="月/年"
                        className="input-field"
                        disabled
                      />
                      <input
                        type="text"
                        placeholder="CVV"
                        className="input-field"
                        disabled
                      />
                      <input
                        type="text"
                        placeholder="持卡人姓名"
                        className="input-field"
                        disabled
                      />
                    </div>
                  </div>
                </div>
              )}

              <div className="flex justify-between">
                {step > 1 && (
                  <button
                    type="button"
                    onClick={() => setStep(step - 1)}
                    className="btn-secondary"
                  >
                    返回
                  </button>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="btn-primary ml-auto"
                >
                  {loading ? '处理中...' : step === 1 ? '继续支付' : '提交订单'}
                </button>
              </div>
            </form>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-soft p-6 sticky top-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">订单摘要</h2>

              <div className="space-y-3 mb-6">
                {cartItems.map((item) => (
                  <div key={item.product_id} className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-gray-100 rounded flex items-center justify-center overflow-hidden">
                      {item.image_url ? (
                        <img
                          src={item.image_url}
                          alt={item.name}
                          className="w-full h-full object-contain rounded"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextElementSibling?.classList.remove('hidden');
                          }}
                        />
                      ) : null}
                      <svg className={`w-6 h-6 text-gray-400 ${item.image_url ? 'hidden' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                      </svg>
                    </div>
                    <div className="flex-1">
                      <h4 className="text-sm font-medium text-gray-900">{item.name}</h4>
                      <p className="text-sm text-gray-500">数量: {item.quantity}</p>
                    </div>
                    <span className="text-sm font-medium text-gray-900">
                      {formatPrice(item.subtotal || item.price * item.quantity)}
                    </span>
                  </div>
                ))}
              </div>

              <div className="border-t border-gray-200 pt-4 space-y-2">
                <div className="flex justify-between text-sm text-gray-600">
                  <span>小计</span>
                  <span>{formatPrice(cartTotal)}</span>
                </div>
                <div className="flex justify-between text-sm text-gray-600">
                  <span>税费 (8%)</span>
                  <span>{formatPrice(tax)}</span>
                </div>
                <div className="flex justify-between text-sm text-gray-600">
                  <span>运费</span>
                  <span>{shipping === 0 ? '免费' : formatPrice(shipping)}</span>
                </div>
                <div className="border-t border-gray-200 pt-2">
                  <div className="flex justify-between text-lg font-semibold text-gray-900">
                    <span>合计</span>
                    <span>{formatPrice(total)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;